/**
 * SiloRenameAudit — silo settings inline tool.
 *
 * When a creator renames their silo, the slug (URL) doesn't auto-update and
 * old user-authored text (lead-ins, placeholder, tagline) may still reference
 * the OLD name. This card:
 *
 *   1. Pings GET /silos/{id}/rename-audit to detect mismatches.
 *   2. Renders a peach advisory card when issues are found.
 *   3. URL finding (ESSENTIAL) — always flags while the slug is wrong, with a
 *      one-click "Refresh URL" button (old links keep redirecting).
 *   4. Text findings (NON-ESSENTIAL) — surface ONCE per rename, each with a
 *      one-click "Auto-fix" button that scrolls to the field and swaps the old
 *      name for the new one. A "Keep as-is" button dismisses them for good.
 *
 * The auto-fix + scroll is handled by the PARENT (SiloSettings) via onFix so it
 * can drive the live form fields + re-save; this component just renders.
 */
import { useEffect, useState } from "react";
import { LinkSimple, Warning, ArrowsClockwise, CheckCircle, PencilSimple, MagicWand } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FIELD_LABELS = {
  slug: "Public URL",
  composer_placeholders: "Public chat placeholder",
  tagline: "Tagline",
};

export default function SiloRenameAudit({ silo, formName, onUpdated, reloadSignal = 0, onFix, onDismiss }) {
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    if (!silo?.id) return;
    setRefreshing(true);
    try {
      const r = await api.get(`/silos/${silo.id}/rename-audit`);
      setAudit(r.data);
    } catch {
      // silently ignore; this is an advisory-only tool
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [silo?.id, silo?.slug, silo?.name, reloadSignal]);

  if (!audit) return null;
  const findings = audit.findings || [];
  const textFindings = findings.filter((f) => f.field !== "slug");
  // Only show the panel when something actually needs attention.
  if (!audit.slug_mismatch && textFindings.length === 0) return null;

  const refreshSlug = async () => {
    if (!window.confirm(
      `Refresh URL?\n\n` +
      `Old: /s/${audit.current_slug}\n` +
      `New: /s/${audit.derived_slug}\n\n` +
      `Old links will keep working — they'll redirect to the new URL automatically. ` +
      `Your share-seed (?ref=…) is unchanged.`
    )) return;
    setBusy(true);
    try {
      const r = await api.post(`/silos/${silo.id}/rename-slug`, { confirm: true });
      if (r.data?.changed) {
        toast.success(`URL updated → /s/${r.data.slug}`);
        onUpdated?.({ ...silo, slug: r.data.slug, previous_slugs: r.data.previous_slugs });
        try {
          window.dispatchEvent(new CustomEvent("mysilo:silo-updated", {
            detail: { siloId: silo.id, slug: r.data.slug, previousSlugs: r.data.previous_slugs },
          }));
        } catch { /* ignore */ }
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not refresh URL.");
    } finally { setBusy(false); }
  };

  const dismissText = async () => {
    setDismissing(true);
    try {
      await api.post(`/silos/${silo.id}/rename-audit/dismiss`);
      onDismiss?.();
      await load();
    } catch {
      toast.error("Could not dismiss.");
    } finally { setDismissing(false); }
  };

  const totalCount = findings.length;

  return (
    <div className="mt-3 bg-[#FFF7F1] border border-[#C16E5A]/30 rounded-md p-3.5" data-testid="silo-rename-audit">
      <div className="flex items-start gap-2.5">
        <Warning size={16} weight="duotone" className="text-[#C16E5A] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#A85E4A] font-medium mb-0.5">
            Rename audit · {totalCount} item{totalCount === 1 ? "" : "s"} need attention
          </div>
          <div className="text-[13px] text-[#1C1B1A]">
            Your silo is named <strong>{audit.current_name}</strong>, but a few places still reference{" "}
            <strong>{audit.legacy_name_guess || "an older name"}</strong>. Tap <strong>Auto-fix</strong> and each one
            jumps to its field and updates itself.
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={refreshing}
          data-testid="silo-rename-audit-reload"
          className="text-[10px] text-[#6B6A68] hover:text-[#1C1B1A] flex items-center gap-1 shrink-0 disabled:opacity-50"
          title="Re-run audit"
        >
          <ArrowsClockwise size={11} className={refreshing ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="mt-3 space-y-2">
        {audit.slug_mismatch && (
          <div className="bg-white border hairline rounded p-2.5 flex items-start gap-2.5" data-testid="rename-audit-item-slug">
            <LinkSimple size={14} weight="duotone" className="text-[#4A5D4E] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0 text-[12px]">
              <div className="text-[#1C1B1A] font-medium flex items-center gap-1.5">
                Public URL
                <span className="text-[8px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-[#C16E5A]/15 text-[#A85E4A]">Required</span>
              </div>
              <div className="text-[#6B6A68] mt-0.5">
                <span className="font-mono">/s/{audit.current_slug}</span> →{" "}
                <span className="font-mono text-[#2F6B3A]">/s/{audit.derived_slug}</span>
              </div>
              <div className="text-[10px] text-[#6B6A68] mt-1 italic">
                Old links keep redirecting — visitors and your share-seeds aren't broken.
              </div>
            </div>
            <button
              type="button"
              onClick={refreshSlug}
              disabled={busy}
              data-testid="silo-rename-audit-refresh-slug"
              className="text-[10px] uppercase tracking-[0.16em] px-2.5 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1 shrink-0"
            >
              <ArrowsClockwise size={11} weight="bold" /> {busy ? "Working…" : "Refresh URL"}
            </button>
          </div>
        )}

        {textFindings.map((f, i) => (
          <div
            key={`${f.field}-${f.index ?? 0}-${i}`}
            className="bg-white border hairline rounded p-2.5 flex items-start gap-2.5"
            data-testid={`rename-audit-item-${f.field}-${f.index ?? 0}`}
          >
            <PencilSimple size={14} weight="duotone" className="text-[#4A5D4E] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0 text-[12px]">
              <div className="text-[#1C1B1A] font-medium">{FIELD_LABELS[f.field] || f.field}</div>
              <div className="text-[#6B6A68] mt-0.5 italic break-words">"{f.current}"</div>
              {f.suggestion !== undefined && (
                <div className="text-[11px] text-[#2F6B3A] mt-0.5 break-words">→ "{f.suggestion}"</div>
              )}
              <div className="text-[10px] text-[#A85E4A] mt-1">{f.context}</div>
            </div>
            <button
              type="button"
              onClick={() => onFix?.(f)}
              data-testid={`rename-audit-fix-${f.field}-${f.index ?? 0}`}
              className="text-[10px] uppercase tracking-[0.16em] px-2.5 py-1.5 rounded-md bg-[#2F6B3A] text-[#FDFCF8] hover:bg-[#255730] flex items-center gap-1 shrink-0"
            >
              <MagicWand size={11} weight="bold" /> Auto-fix
            </button>
          </div>
        ))}
      </div>

      {textFindings.length > 0 && (
        <div className="mt-3 pt-2 border-t border-[#C16E5A]/15 flex items-center justify-between gap-2">
          <span className="text-[10px] text-[#6B6A68] flex items-center gap-1">
            <CheckCircle size={10} weight="duotone" />
            These are optional — we only nudge once per rename.
          </span>
          <button
            type="button"
            onClick={dismissText}
            disabled={dismissing}
            data-testid="rename-audit-dismiss"
            className="text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-md border hairline bg-white text-[#6B6A68] hover:text-[#1C1B1A] hover:border-[#4A5D4E]/40 disabled:opacity-50 shrink-0"
          >
            {dismissing ? "…" : "Keep as-is"}
          </button>
        </div>
      )}
    </div>
  );
}
